import { useId, useState } from 'react';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { useRequestId } from '../../hooks/useRequestId';
import { returnSale } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { Stepper } from '../ui/Stepper';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { today } from '../../core/format/dates';
import { moneyRefundableCents, refundableByMethod, returnLines, returnProblem, returnTotalCents, returnableQty } from '../../core/rules/saleReturn';
import { refundMethodLabel } from './kassaLabels';
import { printReturnCreditNote } from './returnCreditNote';
import type { KuurRefundMethod, Sale } from '../../core/types/kassa';
type Props = {
  sale: Sale;
  onDone: () => void;
};

/**
 * Retour op een bon: kies wat terugkomt, hoe het geld teruggaat en waarom. Kan op elke dag; de
 * retour telt in de kassa van vandaag. Producten gaan standaard terug op de salonvoorraad; vink dat
 * uit bij een kapot of geopend product.
 */
export function SaleReturnForm({
  sale,
  onDone
}: Props) {
  const id = useId();
  const {
    site
  } = useAdmin();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<number, number>>({});
  // Standaard terug via de manier waarmee het meeste binnenkwam (bij gesplitst meestal pin).
  const [method, setMethod] = useState<KuurRefundMethod>(() => {
    const left = refundableByMethod(sale);
    return left.contant > left.pin ? 'contant' : 'pin';
  });
  const [restock, setRestock] = useState(true);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const {
    submit,
    submitting,
    error
  } = useSubmit(returnSale);
  // Per inhoud (regels, manier, reden): een herhaling boekt niet dubbel, een andere retour krijgt een eigen id.
  const requestId = useRequestId(JSON.stringify([qty, method, restock, reason.trim()]));
  const options = sale.lines.map((l, i) => ({
    line: l,
    index: i,
    max: returnableQty(sale, i)
  })).filter(o => o.max > 0);
  if (options.length === 0) return null;
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className={btnSecondary} data-id="element-2593" data-mp="29xwbbj1wed13">
        Retour of terugbetalen
      </button>;
  }
  const inputs = options.map(o => ({
    lineIndex: o.index,
    qty: qty[o.index] ?? 0
  })).filter(l => l.qty > 0);
  const total = returnTotalCents(returnLines(sale, inputs));
  const problem = returnProblem(sale, {
    lines: inputs,
    method,
    reason
  });
  const anyProduct = inputs.some(l => sale.lines[l.lineIndex]?.kind === 'product');
  const methods: KuurRefundMethod[] = sale.customerId ? ['pin', 'contant', 'tegoed'] : ['pin', 'contant'];
  const otherDay = sale.day !== today();
  const confirm = async () => {
    setTouched(true);
    if (problem) return;
    const done = await submit(sale.id, {
      requestId,
      lines: inputs,
      method,
      restock: anyProduct && restock,
      reason: reason.trim()
    });
    if (!done) return;
    toast.success(`Retour geboekt · ${done.number}`, {
      description: method === 'tegoed' ? `${money(done.refundCents)} staat op haar tegoed.` : `Geef ${money(done.refundCents)} terug ${method === 'pin' ? 'via de pinautomaat' : 'contant'}. Het telt in de kassa van vandaag.`,
      action: {
        label: 'Creditnota',
        onClick: () => {
          void printReturnCreditNote(done, site.salon).then(ok => {
            if (!ok) toast.error('Creditnota openen lukte niet', {
              description: 'Probeer het opnieuw of gebruik een andere browser.'
            });
          });
        }
      }
    });
    onDone();
  };
  return <fieldset className="space-y-4 rounded-md bg-white p-4" aria-describedby={`${id}-hint`} data-id="element-2594" data-mp="5fy5kw0jjmz3">
      <legend className="sr-only" data-id="element-2595" data-mp="adq6ugn2slfy">Retour op bon {sale.number}</legend>
      <div data-id="element-2596" data-mp="1zrv2h4hr5l1j">
        <p className="text-[14px] text-ink" data-id="element-2597" data-mp="1ztfwmt05n1yv">Wat komt er terug?</p>
        <p id={`${id}-hint`} className="text-[12px] text-muted" data-id="element-2598" data-mp="24qxvfqkgk0t5">
          {otherDay ? 'De bon is van een eerdere dag: de retour telt vandaag, met een eigen creditnota.' : 'Een eigen creditnota; de bon blijft staan.'}
        </p>
      </div>

      <ul className="space-y-2" data-id="element-2599" data-mp="3vvsq4r6738fg">
        {options.map(o => <li key={o.index} className="flex items-center justify-between gap-3" data-id="element-2600" data-mp="3qvn3jpes8c48">
            <span className="min-w-0" data-id="element-2601" data-mp="3muuw91ph0hal">
              <span className="block truncate text-[14px] text-ink" data-id="element-2602" data-mp="3mtt7jufj7wae">{o.line.name}</span>
              <span className="block text-[12px] text-muted" data-id="element-2603" data-mp="3hwjmeqz6e6sd">
                {money(o.line.priceCents)} {o.line.kind === 'product' ? `per stuk · nog ${o.max} terug te nemen` : 'betaald'}
              </span>
            </span>
            {o.line.kind === 'product' && o.max > 1 ? <Stepper label={`Terug: ${o.line.name}`} value={qty[o.index] ?? 0} min={0} max={o.max} onChange={v => setQty(q => ({
          ...q,
          [o.index]: v
        }))} data-id="element-2604" data-mp="3373vnazna1co" /> : <input type="checkbox" aria-label={`${o.line.name} terug`} checked={(qty[o.index] ?? 0) > 0} onChange={e => setQty(q => ({
          ...q,
          [o.index]: e.target.checked ? 1 : 0
        }))} className="h-4 w-4 accent-ink" data-id="element-2605" data-mp="2u5xbmrxse8eo" />}
          </li>)}
      </ul>

      <div role="radiogroup" aria-label="Hoe gaat het geld terug?" className="flex flex-wrap gap-2" data-id="element-2606" data-mp="1ipcm8btnbtdx">
        {methods.map(m => <button key={m} type="button" role="radio" aria-checked={method === m} onClick={() => setMethod(m)} className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors duration-150 ease-soft ${method === m ? 'border-ink bg-ink text-white' : 'border-line text-ink hover:border-ink'}`} data-id="element-2607" data-mp="qxdl7b1ne0ef">
            {refundMethodLabel[m]}
          </button>)}
      </div>
      {method !== 'tegoed' && refundableByMethod(sale)[method] < sale.totalCents && <p className="text-[12px] text-muted" data-id="element-2608" data-mp="c76a5gdyzjnr">
          Via {method} hooguit {money(refundableByMethod(sale)[method])}
          {moneyRefundableCents(sale) < sale.totalCents ? ': de rest is met cadeaubon, tegoed of een andere manier betaald.' : ': de rest kwam via een andere manier binnen.'}
        </p>}

      {anyProduct && <label className="flex items-start gap-2 text-[13px] text-ink" data-id="element-2609" data-mp="1odja7i5olai8">
          <input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} className="mt-0.5 h-4 w-4 accent-ink" data-id="element-2610" data-mp="1kamx26ywu6lo" />
          <span data-id="element-2611" data-mp="rlft2sgosot1">
            Terug op de salonvoorraad
            <span className="block text-[12px] text-muted" data-id="element-2612" data-mp="rn0mufv6g3pu">Uit bij een kapot of geopend product: dan blijft het afgeboekt.</span>
          </span>
        </label>}

      <label htmlFor={`${id}-reason`} className="block" data-id="element-2613" data-mp="10luowlra1mas">
        <span className="mb-1 block text-[13px] text-ink" data-id="element-2614" data-mp="10ogxmopwm7ul">Reden <span className="text-muted" data-id="element-2615" data-mp="2gzcexxajez7n">(komt op de creditnota)</span></span>
        <input id={`${id}-reason`} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} placeholder="Bijv. reageerde niet goed op het serum" className={inputCls} data-id="element-2616" data-mp="2c1ss48zrxe43" />
      </label>

      {(touched && problem || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-2617" data-mp="2ol7ylxxbjrim">
          {error ?? problem?.message}
        </p>}

      <div className="flex flex-wrap items-center gap-2" data-id="element-2618" data-mp="2am86uheiewi8">
        <button type="button" onClick={() => void confirm()} disabled={submitting || total === 0} aria-busy={submitting} className={btnPrimary} data-id="element-2619" data-mp="2fk9vdhupfo13">
          {submitting ? 'Boeken…' : total > 0 ? `${money(total)} terug · boeken` : 'Kies wat terugkomt'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={submitting} className={btnGhost} data-id="element-2620" data-mp="2tj9ek9zg7apv">
          Annuleren
        </button>
      </div>
    </fieldset>;
}